import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, Flex, Field, IconButton } from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import {
  Button as ButtonComponent,
  InputField,
  TextareaField,
  StyledCheckbox,
  ToggleGroup,
} from '@themes/aegix';
import { FiEdit } from 'react-icons/fi';
import { RiCloseCircleLine } from 'react-icons/ri';
import { HiPlus, HiEye, HiPencil } from 'react-icons/hi2';

export interface GuidanceStepLocal {
  id?: number;
  row_type: 'step' | 'header';
  title: string;
  description?: string;
  order: number;
  is_tick_required: boolean;
  _isNew?: boolean;
  _isDeleted?: boolean;
}

export interface GuidanceLocal {
  id?: number;
  title: string;
  plan: string;
  is_default?: boolean;
  steps?: GuidanceStepLocal[];
  _isNew?: boolean;
  _isDeleted?: boolean;
}

interface ProtocolGuidanceEditorProps {
  guidances: GuidanceLocal[];
  onGuidancesChange: (guidances: GuidanceLocal[]) => void;
}

export function ProtocolGuidanceEditor({
  guidances,
  onGuidancesChange,
}: ProtocolGuidanceEditorProps) {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');

  // Guidance form state
  const [title, setTitle] = useState('');
  const [plan, setPlan] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [steps, setSteps] = useState<GuidanceStepLocal[]>([]);

  // Step editor state
  const [isStepDrawerOpen, setIsStepDrawerOpen] = useState(false);
  const [editStepIndex, setEditStepIndex] = useState<number | null>(null);
  const [stepRowType, setStepRowType] = useState<'step' | 'header'>('step');
  const [stepTitle, setStepTitle] = useState('');
  const [stepDescription, setStepDescription] = useState('');
  const [stepTickRequired, setStepTickRequired] = useState(true);

  const activeGuidances = guidances.filter((g) => !g._isDeleted);

  const resetForm = () => {
    setTitle('');
    setPlan('');
    setIsDefault(false);
    setSteps([]);
    setViewMode('edit');
    setEditIndex(null);
  };

  const handleAddGuidance = () => {
    resetForm();
    setIsDrawerOpen(true);
  };

  const handleEditGuidance = (index: number) => {
    const g = activeGuidances[index];
    setTitle(g.title);
    setPlan(g.plan);
    setIsDefault(g.is_default ?? false);
    setSteps(g.steps ?? []);
    setEditIndex(index);
    setViewMode('edit');
    setIsDrawerOpen(true);
  };

  const handleDeleteGuidance = (index: number) => {
    const g = activeGuidances[index];
    const originalIndex = guidances.findIndex((og) => og === g);
    if (g.id) {
      const updated = [...guidances];
      updated[originalIndex] = { ...g, _isDeleted: true };
      onGuidancesChange(updated);
    } else {
      onGuidancesChange(guidances.filter((_, i) => i !== originalIndex));
    }
  };

  const handleSaveGuidance = () => {
    if (!title.trim()) return;

    const guidanceData: GuidanceLocal = {
      title: title.trim(),
      plan: plan.trim(),
      is_default: isDefault,
      steps: steps.filter((s) => !s._isDeleted),
    };

    if (editIndex !== null) {
      const g = activeGuidances[editIndex];
      const originalIndex = guidances.findIndex((og) => og === g);
      const updated = [...guidances];
      updated[originalIndex] = { ...g, ...guidanceData };
      onGuidancesChange(updated);
    } else {
      onGuidancesChange([...guidances, { ...guidanceData, _isNew: true }]);
    }

    setIsDrawerOpen(false);
    resetForm();
  };

  // Step handlers
  const activeSteps = steps.filter((s) => !s._isDeleted);

  const resetStepForm = () => {
    setStepRowType('step');
    setStepTitle('');
    setStepDescription('');
    setStepTickRequired(true);
    setEditStepIndex(null);
  };

  const handleAddStep = () => {
    resetStepForm();
    setIsStepDrawerOpen(true);
  };

  const handleEditStep = (idx: number) => {
    const s = activeSteps[idx];
    setStepRowType(s.row_type);
    setStepTitle(s.title);
    setStepDescription(s.description ?? '');
    setStepTickRequired(s.is_tick_required);
    setEditStepIndex(idx);
    setIsStepDrawerOpen(true);
  };

  const handleDeleteStep = (idx: number) => {
    const s = activeSteps[idx];
    const origIdx = steps.findIndex((os) => os === s);
    if (s.id) {
      const updated = [...steps];
      updated[origIdx] = { ...s, _isDeleted: true };
      setSteps(updated);
    } else {
      setSteps(steps.filter((_, i) => i !== origIdx));
    }
  };

  const handleSaveStep = () => {
    if (!stepTitle.trim()) return;

    const stepData: GuidanceStepLocal = {
      row_type: stepRowType,
      title: stepTitle.trim(),
      description: stepDescription.trim() || undefined,
      order: editStepIndex !== null ? activeSteps[editStepIndex].order : activeSteps.length,
      is_tick_required: stepRowType === 'step' ? stepTickRequired : false,
    };

    if (editStepIndex !== null) {
      const s = activeSteps[editStepIndex];
      const origIdx = steps.findIndex((os) => os === s);
      const updated = [...steps];
      updated[origIdx] = { ...s, ...stepData };
      setSteps(updated);
    } else {
      setSteps([...steps, { ...stepData, _isNew: true }]);
    }

    setIsStepDrawerOpen(false);
    resetStepForm();
  };

  const handleMoveStep = (idx: number, direction: 'up' | 'down') => {
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= activeSteps.length) return;

    const origIdx1 = steps.findIndex((os) => os === activeSteps[idx]);
    const origIdx2 = steps.findIndex((os) => os === activeSteps[swapIdx]);
    const updated = [...steps];
    const order1 = updated[origIdx1].order;
    updated[origIdx1] = { ...updated[origIdx1], order: updated[origIdx2].order };
    updated[origIdx2] = { ...updated[origIdx2], order: order1 };
    setSteps(updated);
  };

  return (
    <Box>
      {activeGuidances.length === 0 ? (
        <Text fontSize="sm" color="text.secondary" mb={3}>
          {t('protocols.noGuidanceEntries', 'No guidance entries yet.')}
        </Text>
      ) : (
        <Box mb={3}>
          {activeGuidances.map((g, i) => (
            <Flex
              key={g.id ?? `new-${i}`}
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2}
              mb={1}
              bg="surface.secondary"
              borderRadius="md"
            >
              <Flex alignItems="center" gap={2}>
                <Text fontSize="sm" color="text.primary" fontWeight={500}>
                  {g.title}
                </Text>
                {g.is_default && (
                  <Text fontSize="xs" color="text.secondary">
                    {t('protocols.defaultBadge', '(default)')}
                  </Text>
                )}
              </Flex>
              <Flex gap={1}>
                <IconButton
                  aria-label={t('protocols.editGuidanceAria', 'Edit guidance')}
                  variant="ghost"
                  size="xs"
                  _hover={{ color: 'brand.500' }}
                  onClick={() => handleEditGuidance(i)}
                  data-e2e={`edit-guidance-button-${i}`}
                >
                  <FiEdit size={14} />
                </IconButton>
                <IconButton
                  aria-label={t('protocols.deleteGuidanceAria', 'Delete guidance')}
                  variant="ghost"
                  size="xs"
                  color="text.danger"
                  _hover={{ color: 'red.600' }}
                  onClick={() => handleDeleteGuidance(i)}
                  data-e2e={`delete-guidance-button-${i}`}
                >
                  <RiCloseCircleLine size={16} />
                </IconButton>
              </Flex>
            </Flex>
          ))}
        </Box>
      )}

      <ButtonComponent visual="secondary" size="sm" onClick={handleAddGuidance} data-e2e="add-guidance-button">
        <HiPlus size={14} />
        <Text ml={1}>{t('protocols.addGuidance', 'Add Guidance')}</Text>
      </ButtonComponent>

      {/* Guidance edit drawer */}
      <DrawerComponent
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          resetForm();
        }}
        title={editIndex !== null ? t('protocols.editGuidance', 'Edit Guidance') : t('protocols.addGuidance', 'Add Guidance')}
        size="md"
        e2eTitle="guidance-edit-drawer"
        footer={
          <Flex gap={3} width="100%">
            <ButtonComponent
              visual="secondary"
              onClick={() => {
                setIsDrawerOpen(false);
                resetForm();
              }}
              flex={1}
              data-e2e="cancel-button">
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent visual="primary" onClick={handleSaveGuidance} flex={1} data-e2e="editindex--null--save--add-button">
              {editIndex !== null ? t('common.save', 'Save') : t('common.add', 'Add')}
            </ButtonComponent>
          </Flex>
        }
      >
        <Box>
          <Field.Root mb={4}>
            <Field.Label htmlFor="guidance-title">{t('common.title', 'Title')}</Field.Label>
            <InputField
              id="guidance-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('protocols.guidanceTitlePlaceholder', 'Audience name (e.g., Teachers, Staff)')}
              data-e2e="guidance-title-input"
            />
          </Field.Root>

          <Field.Root mb={4}>
            <Flex justifyContent="space-between" alignItems="center" mb={1}>
              <Field.Label htmlFor="guidance-plan">{t('protocols.plan', 'Plan')}</Field.Label>
              <Flex gap={1}>
                <IconButton
                  aria-label={t('protocols.editPlanAria', 'Edit plan')}
                  aria-pressed={viewMode === 'edit'}
                  variant="ghost"
                  size="xs"
                  bg={viewMode === 'edit' ? 'surface.tertiary' : 'transparent'}
                  onClick={() => setViewMode('edit')}
                >
                  <HiPencil size={14} />
                </IconButton>
                <IconButton
                  aria-label={t('protocols.previewPlanAria', 'Preview plan')}
                  aria-pressed={viewMode === 'preview'}
                  variant="ghost"
                  size="xs"
                  bg={viewMode === 'preview' ? 'surface.tertiary' : 'transparent'}
                  onClick={() => setViewMode('preview')}
                  data-e2e="guidance-preview-mode"
                >
                  <HiEye size={14} />
                </IconButton>
              </Flex>
            </Flex>

            {viewMode === 'edit' ? (
              <TextareaField
                id="guidance-plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                placeholder={t('protocols.guidancePlanPlaceholder', 'Guidance plan (markdown supported)')}
                minH="120px"
                data-e2e="guidance-plan-markdown-supported-input" />
            ) : (
              <Box
                p={3}
                bg="surface.secondary"
                borderRadius="md"
                minH="120px"
                fontSize="sm"
                whiteSpace="pre-wrap"
              >
                {plan || t('common.noContent', 'No content')}
              </Box>
            )}
          </Field.Root>

          <Field.Root mb={4}>
            <Flex
              role="checkbox"
              aria-checked={isDefault}
              tabIndex={0}
              alignItems="center"
              gap={2}
              cursor="pointer"
              onClick={() => setIsDefault(!isDefault)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  setIsDefault(!isDefault);
                }
              }}
              data-e2e="default-guidance-checkbox"
            >
              <StyledCheckbox checked={isDefault} />
              <Text fontSize="sm" color="text.primary">
                {t('protocols.defaultGuidance', 'Default Guidance')}
              </Text>
            </Flex>
          </Field.Root>

          {/* Steps section */}
          <Text fontSize="sm" fontWeight={600} color="text.primary" mb={2}>
            {t('protocols.steps', 'Steps')}
          </Text>

          {activeSteps.length > 0 && (
            <Box mb={3}>
              {activeSteps
                .sort((a, b) => a.order - b.order)
                .map((step, i) => (
                  <Flex
                    key={step.id ?? `step-${i}`}
                    alignItems="center"
                    justifyContent="space-between"
                    px={3}
                    py={2}
                    mb={1}
                    bg={step.row_type === 'header' ? 'surface.tertiary' : 'surface.secondary'}
                    borderRadius="md"
                    data-e2e={`guidance-step-row-${i}`}
                  >
                    <Flex alignItems="center" gap={2} flex={1}>
                      {step.row_type === 'step' && step.is_tick_required && (
                        <Box
                          w="14px"
                          h="14px"
                          borderRadius="sm"
                          border="2px solid"
                          borderColor="border.secondary"
                        />
                      )}
                      <Text
                        fontSize="sm"
                        fontWeight={step.row_type === 'header' ? 600 : 400}
                        color="text.primary"
                      >
                        {step.title}
                      </Text>
                    </Flex>
                    <Flex gap={1} alignItems="center">
                      {i > 0 && (
                        <IconButton
                          aria-label={t('protocols.moveStepUpAria', 'Move step up')}
                          variant="ghost"
                          size="xs"
                          fontSize="xs"
                          onClick={() => handleMoveStep(i, 'up')}
                          data-e2e={`move-step-up-${i}`}
                        >
                          ↑
                        </IconButton>
                      )}
                      {i < activeSteps.length - 1 && (
                        <IconButton
                          aria-label={t('protocols.moveStepDownAria', 'Move step down')}
                          variant="ghost"
                          size="xs"
                          fontSize="xs"
                          onClick={() => handleMoveStep(i, 'down')}
                          data-e2e={`move-step-down-${i}`}
                        >
                          ↓
                        </IconButton>
                      )}
                      <IconButton
                        aria-label={t('protocols.editStepAria', 'Edit step')}
                        variant="ghost"
                        size="xs"
                        _hover={{ color: 'brand.500' }}
                        onClick={() => handleEditStep(i)}
                        data-e2e={`edit-step-button-${i}`}
                      >
                        <FiEdit size={12} />
                      </IconButton>
                      <IconButton
                        aria-label={t('protocols.deleteStepAria', 'Delete step')}
                        variant="ghost"
                        size="xs"
                        color="text.danger"
                        _hover={{ color: 'red.600' }}
                        onClick={() => handleDeleteStep(i)}
                        data-e2e={`delete-step-button-${i}`}
                      >
                        <RiCloseCircleLine size={14} />
                      </IconButton>
                    </Flex>
                  </Flex>
                ))}
            </Box>
          )}

          <ButtonComponent visual="secondary" size="sm" onClick={handleAddStep} data-e2e="add-step-button">
            <HiPlus size={14} />
            <Text ml={1}>{t('protocols.addStep', 'Add Step')}</Text>
          </ButtonComponent>
        </Box>
      </DrawerComponent>

      {/* Step edit drawer */}
      <DrawerComponent
        isOpen={isStepDrawerOpen}
        onClose={() => {
          setIsStepDrawerOpen(false);
          resetStepForm();
        }}
        title={editStepIndex !== null ? t('protocols.editStep', 'Edit Step') : t('protocols.addStep', 'Add Step')}
        size="sm"
        e2eTitle="step-edit-drawer"
        footer={
          <Flex gap={3} width="100%">
            <ButtonComponent
              visual="secondary"
              onClick={() => {
                setIsStepDrawerOpen(false);
                resetStepForm();
              }}
              flex={1}
              data-e2e="cancel-button">
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent visual="primary" onClick={handleSaveStep} flex={1} data-e2e="save-button">
              {editStepIndex !== null ? t('common.save', 'Save') : t('common.add', 'Add')}
            </ButtonComponent>
          </Flex>
        }
      >
        <Box>
          <Field.Root mb={4}>
            <Field.Label>{t('common.type', 'Type')}</Field.Label>
            <ToggleGroup
              options={[
                { value: 'step', label: t('protocols.stepTypeTickable', 'Step (Tickable)'), 'data-e2e': 'step-type-step' },
                { value: 'header', label: t('protocols.stepTypeHeader', 'Header (Section)'), 'data-e2e': 'step-type-header' },
              ]}
              value={stepRowType}
              onChange={(value) => setStepRowType(value as 'step' | 'header')}
              // Matches the Field.Label directly above it.
              aria-label={t('common.type', 'Type')}
              data-e2e="step-type-toggle"
            />
          </Field.Root>

          <Field.Root mb={4}>
            <Field.Label htmlFor="step-title">{t('common.title', 'Title')}</Field.Label>
            <InputField
              id="step-title"
              value={stepTitle}
              onChange={(e) => setStepTitle(e.target.value)}
              placeholder={stepRowType === 'header' ? t('protocols.sectionNamePlaceholder', 'Section name') : t('protocols.stepDescriptionPlaceholder', 'Step description')}
              data-e2e="step-title-input"
            />
          </Field.Root>

          <Field.Root mb={4}>
            <Field.Label htmlFor="step-description">{t('common.description', 'Description')}</Field.Label>
            <TextareaField
              id="step-description"
              value={stepDescription}
              onChange={(e) => setStepDescription(e.target.value)}
              placeholder={t('protocols.optionalDescriptionPlaceholder', 'Optional description')}
              minH="60px"
              data-e2e="step-description-textarea"
            />
          </Field.Root>

          {stepRowType === 'step' && (
            <Field.Root>
              <Flex
                role="checkbox"
                aria-checked={stepTickRequired}
                tabIndex={0}
                alignItems="center"
                gap={2}
                cursor="pointer"
                onClick={() => setStepTickRequired(!stepTickRequired)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setStepTickRequired(!stepTickRequired);
                  }
                }}
                data-e2e="step-tick-required-checkbox"
              >
                <StyledCheckbox checked={stepTickRequired} />
                <Text fontSize="sm" color="text.primary">
                  {t('protocols.requireTick', 'Require Tick')}
                </Text>
              </Flex>
            </Field.Root>
          )}
        </Box>
      </DrawerComponent>
    </Box>
  );
}
